/* eslint-disable jsx-a11y/no-static-element-interactions */
import classNames from 'classnames';
import type { FC } from 'react';
import React, { useCallback, useContext } from 'react';

import { RicosContext } from './../../ricos-context';
import type { ButtonData } from './../../ricos-schema/legacy/wix/rich_content/v1';
import { ButtonData_Type, Node_Type } from './../../ricos-schema/legacy/wix/rich_content/v1';
import type { Helpers, LegacyEditorPluginConfig } from './../../ricos-types';
import { getLinkHrefUrl } from './../../_shared/link/get-link-href-url';
import { getRelValue } from './../../wix-rich-content-common';

import { ROOT } from '../data-hooks';
import styles from '../statics/styles/default-styles-scss';
import { ACTION_BUTTON_TYPE, LINK_BUTTON_TYPE } from '../types';

interface ButtonViewerProps {
  onClick?: () => void;
  className?: string;
  helpers?: Helpers;
  settings: LegacyEditorPluginConfig;
  buttonData: ButtonData;
  nodeId: string;
  baseUrl?: string;
}

export const ButtonViewer: FC<ButtonViewerProps> = ({
  onClick,
  className,
  helpers,
  settings = {},
  buttonData,
  nodeId,
  baseUrl,
}) => {
  const { tabIndex } = useContext(RicosContext);
  const isActionButton = buttonData.type === ButtonData_Type.ACTION;
  const onClickHandler = useCallback(() => {
    helpers?.onViewerAction?.(
      isActionButton ? ACTION_BUTTON_TYPE : LINK_BUTTON_TYPE,
      'Click',
      '',
      nodeId,
      {
        type: Node_Type.BUTTON,
        buttonData,
      }
    );
    onClick?.();
  }, [helpers?.onViewerAction]);
  const ButtonComponent = settings.customComponent
    ? settings.customComponent
    : isActionButton
    ? ActionButton
    : LinkButton;

  const style: React.CSSProperties = {
    color: buttonData?.styles?.colors?.text ?? settings.colors?.color1,
    background: buttonData?.styles?.colors?.background ?? settings.colors?.color8,
    borderRadius: buttonData.styles?.border?.radius,
    width: buttonData.containerData?.width?.custom
      ? `min(100%, ${buttonData.containerData?.width?.custom}px)`
      : undefined,
    border: `${buttonData.styles?.border?.width || 0}px solid ${
      buttonData?.styles?.colors?.border ?? settings.colors?.color8 ?? ''
    }`,
  };

  return (
    <ButtonComponent
      className={classNames(styles.button_container, className)}
      style={style}
      data-hook={ROOT}
      onClick={onClickHandler}
      tabIndex={tabIndex}
      buttonData={buttonData}
      {...(!isActionButton && {
        href: buttonData.link ? getLinkHrefUrl(buttonData.link, baseUrl) : undefined,
        target: buttonData.link?.target && `_${buttonData.link.target.toLowerCase()}`,
        rel: buttonData.link?.rel?.nofollow ? getRelValue('nofollow') : undefined,
      })}
    />
  );
};

const LinkButton: React.FC<ButtonViewerProps> = props => {
  const { buttonData, ...rest } = props;
  return (
    <a
      {...rest}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          props.onClick?.();
        }
      }}
    >
      <div className={styles.button_text}>{buttonData.text}</div>
    </a>
  );
};

const ActionButton: React.FC<ButtonViewerProps> = props => {
  const { buttonData, ...rest } = props;
  return (
    <button
      {...rest}
      type="button"
      onKeyDown={e => {
        if (e.key === ' ' || e.key === 'Enter') {
          props.onClick?.();
        }
      }}
    >
      <div className={styles.button_text}>{buttonData.text}</div>
    </button>
  );
};
